import { useRef, useState } from 'react';
import { isBefore, startOfDay } from 'date-fns';

import { Button, DateRangePicker, Icon, RadioGroup } from '@components/index';
import { DatePickerType } from '@constants/groups';
import { customDefaultDateRange } from '@features/dashboard/constants';
import { ReportDateRangeOptionEnum } from '@features/reports/constants';
import {
  getCurrentFinancialYear,
  getLastFinancialYear
} from '@features/reports/helper';
import useScrollIntoViewWithHeader from '@hooks/useScrollIntoViewWithHeader';
import { DateRange, DisabledDateData, Option } from '@types';
import {
  formatISODate,
  getStartAndEndDateOfLastMonth,
  getStartAndPresentDateOfCurrentMonth
} from '@utils/date';
import useOnClickOutside from '@utils/hooks';

export interface ReportDateRangePickerProps {
  currentFilter: string;
  filterOptions: Array<Option>;
  onSelectFilter: (dateRange: DateRange, dateOption: string) => void;
  customClass?: string;
  customDropdownClass?: string;
  buttonClassName?: string;
  minDate?: Date;
  disabledDateData?: DisabledDateData;
}

const ReportDateRangePicker = (props: ReportDateRangePickerProps) => {
  const { startDate, endDate } = getStartAndPresentDateOfCurrentMonth();

  const {
    currentFilter,
    filterOptions,
    onSelectFilter,
    customClass,
    customDropdownClass,
    buttonClassName,
    minDate,
    disabledDateData
  } = props;
  const [isDropdownOpen, setIsDropdownOpen] = useState<boolean>(false);
  const [selectedDateOption, setSelectedDateOption] =
    useState<string>(currentFilter);
  const [selectedDateRange, setSelectedDateRange] = useState<DateRange>({
    startDate,
    endDate
  });
  const [showDatePicker, setShowDatePicker] = useState<boolean>(false);

  const ref = useRef(null);
  const dropdownRef = useRef<HTMLDivElement>(null); // Add this ref

  const toggleDropdown = () => {
    setIsDropdownOpen((isOpen) => !isOpen);
  };

  const effectiveMinDate = disabledDateData?.minDate ?? minDate;

  const clampDateRangeToMin = (dateRange: DateRange): DateRange => {
    if (!effectiveMinDate) {
      return dateRange;
    }

    const minDay = startOfDay(effectiveMinDate);
    const start = isBefore(startOfDay(dateRange.startDate), minDay)
      ? minDay
      : dateRange.startDate;

    return { startDate: start, endDate: dateRange.endDate };
  };

  const getDefaultFilterOption = () =>
    filterOptions[0]?.key || ReportDateRangeOptionEnum.THIS_MONTH;

  const onSelectOption = (dateOption: string) => {
    setSelectedDateOption(dateOption);
    let selectedDate: DateRange;
    switch (dateOption) {
      case ReportDateRangeOptionEnum.THIS_MONTH: {
        selectedDate = getStartAndPresentDateOfCurrentMonth();
        break;
      }
      case ReportDateRangeOptionEnum.LAST_MONTH: {
        selectedDate = getStartAndEndDateOfLastMonth();
        break;
      }
      case ReportDateRangeOptionEnum.CURRENT_FINANCIAL_YEAR: {
        selectedDate = getCurrentFinancialYear();
        break;
      }
      case ReportDateRangeOptionEnum.LAST_FINANCIAL_YEAR: {
        selectedDate = getLastFinancialYear();
        break;
      }
      default: {
        selectedDate = customDefaultDateRange;
        setShowDatePicker(true);
      }
    }
    const clampedDateRange = clampDateRangeToMin(selectedDate);
    setSelectedDateRange(clampedDateRange);
    onSelectFilter(clampedDateRange, dateOption);
  };

  const selectedFilter: String | undefined = filterOptions?.find(
    (option) => option.key === currentFilter
  )?.value;

  const dateRangeText =
    // eslint-disable-next-line no-nested-ternary
    selectedDateOption
      ? selectedDateOption === ReportDateRangeOptionEnum.CUSTOM
        ? 'Custom Date Range'
        : selectedFilter
      : 'Select Date Range';

  const dateRangeValue = `${formatISODate({
    date: selectedDateRange.startDate,
    format: 'DD MMM YYYY'
  })} - ${formatISODate({
    date: selectedDateRange.endDate,
    format: 'DD MMM YYYY'
  })}`;

  useOnClickOutside(ref, (e) => {
    e.stopPropagation();
    setIsDropdownOpen(false);
    setShowDatePicker(false);
    if (selectedDateOption === ReportDateRangeOptionEnum.CUSTOM)
      onSelectFilter(selectedDateRange, ReportDateRangeOptionEnum.CUSTOM);
  });

  useScrollIntoViewWithHeader({
    elementRef: dropdownRef, // Use ref instead of getElementById
    shouldScroll: isDropdownOpen
  });

  return (
    <div className="relative" ref={ref}>
      <div className={`flex w-105 items-center gap-2 ${customClass}`}>
        <Button
          variant="transparent"
          handleClick={toggleDropdown}
          size="medium"
          className={`h-12.5 w-full min-w-166 justify-between border border-cofeeNeutral5 !bg-cofeeNeutral1 hover:border-cofeeNeutral7 ${buttonClassName}`}
        >
          <div className="flex w-full items-center justify-between text-base">
            <div
              className={`${selectedDateOption ? 'text-content' : 'text-grey'}`}
            >
              {dateRangeText}
            </div>
            <div className="flex gap-2 text-sm text-grey">
              {selectedDateRange && selectedDateOption && dateRangeValue}
              <Icon
                name="down-icon-dark"
                className={`transition-transform duration-300 ease-in-out ${
                  isDropdownOpen ? '-rotate-180' : ''
                }`}
              />
            </div>
          </div>
        </Button>
      </div>
      {isDropdownOpen &&
        (!showDatePicker ? (
          <div
            ref={dropdownRef}
            className={`absolute right-0 z-20 mt-1 w-full origin-top-right rounded-xl bg-theme p-6 shadow-dropdownShadow `}
            id="dropdown-list"
          >
            <RadioGroup
              options={filterOptions}
              handleChange={onSelectOption}
              orientation="vertical"
              selectedValue={selectedDateOption}
              labelStyle="w-full"
            />
            <div
              className="flex cursor-pointer items-center justify-between border-t pt-4"
              role="presentation"
              onClick={() => {
                setShowDatePicker(true);
                setSelectedDateOption(ReportDateRangeOptionEnum.CUSTOM);
                setSelectedDateRange(
                  clampDateRangeToMin(customDefaultDateRange)
                );
              }}
            >
              Custom Date Range
              <Icon name="right" />
            </div>
          </div>
        ) : (
          <div
            className={`absolute z-10 bg-white ${customDropdownClass || ''}`}
          >
            <DateRangePicker
              cancelButtonLabel="Cancel"
              confirmButtonLabel="Apply"
              handleConfirm={(dateRange) => {
                if (!dateRange?.startDate || !dateRange?.endDate) {
                  return;
                }

                const clampedDateRange = clampDateRangeToMin(dateRange);
                setSelectedDateRange(clampedDateRange);
                onSelectFilter(
                  clampedDateRange,
                  ReportDateRangeOptionEnum.CUSTOM
                );
                setShowDatePicker(false);
                setIsDropdownOpen(false);
              }}
              handleCancel={() => {
                setShowDatePicker(false);
                onSelectOption(getDefaultFilterOption());
              }}
              defaultDatePickerType={DatePickerType.CUSTOM}
              showPicker={false}
              defaultDateRange={selectedDateRange}
              minDate={minDate}
              maxDate={new Date()}
              disabledDateData={disabledDateData}
              className="shadow-dropdownShadow"
              showHeading
              showTitle={false}
              title="Custom Date Range"
              customPickerStyle="px-4"
              confirmButtonStyle="!text-primary"
            />
          </div>
        ))}
    </div>
  );
};

export default ReportDateRangePicker;
